import React from 'react';

interface TokenCostBadgeProps {
  /** Custo em tokens da consulta/tiragem. Hoje é sempre 1 no backend, mas o componente aceita qualquer valor. */
  cost?: number;
  /** true quando a consulta é gratuita agora (ex.: Tarot do Dia com a promoção ativa). */
  free?: boolean;
  /** Saldo atual do usuário — usado só para decidir a cor (cinza quando insuficiente). Opcional. */
  balance?: number;
  className?: string;
}

/**
 * Selo de custo em tokens, reutilizável em cards de consulta e tiragens.
 * Não decide se o card fica desabilitado — isso é responsabilidade de quem o usa
 * (ver `canAfford`), porque o estado desabilitado também precisa trocar o CTA
 * para "Comprar tokens", o que varia por contexto.
 */
const TokenCostBadge: React.FC<TokenCostBadgeProps> = ({ cost = 1, free = false, balance, className = '' }) => {
  if (free) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${className}`}
        style={{ background: 'rgba(34,197,94,0.15)', color: '#86efac', border: '1px solid rgba(34,197,94,0.3)' }}
      >
        Grátis hoje
      </span>
    );
  }

  const insufficient = typeof balance === 'number' && balance < cost;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${className}`}
      style={
        insufficient
          ? { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.1)' }
          : { background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }
      }
    >
      ✨ {cost} token{cost > 1 ? 's' : ''}
    </span>
  );
};

/** Retorna true se o saldo cobre o custo (consultas gratuitas sempre "cabem"). */
export const canAfford = (balance: number | undefined, cost = 1, free = false): boolean => {
  if (free) return true;
  return (balance ?? 0) >= cost;
};

export default TokenCostBadge;
